import { useParams, BCMSRecord, BCMSStateManagerCRUD, StateManager, useBCMSContext, Link, DisabledPrompt, useModel, useRoute, useStateManager, useWorkflow } from '@bcm-dev/bcms-vite';
import FormContainer, { FormButtonContainer, FormRow } from "components/shared/containers/FormContainer";
import { UICancelButton, UIPrimaryButton } from "components/ui/UIButton";
import UIRadio from "components/ui/UIRadio";
import React, { useEffect, useState } from "react";
import { SVGAsset } from "res";
import "./styles.css";
import Classes, { CardOnFileMerchant, RecurringPaymentMerchant } from 'model';

/**
 * @function ReceiveTokenView
 * @param {STRING} email - email tied to account
 * @param {STRING} phone - phone number tied to account
 * @return {React.Component}
 * @exported true
 * @description The main component for ReceiveTokenView
 * @example
 * <ReceiveTokenView email="abc@email.com" phone="123-456-7890" />
 */
export default function ReceiveTokenView(props) {
  const globalController = useBCMSContext();
  BCMSRecord.globalControllerFor(Classes, globalController);
  const { goStep, nextStep, prevStep } = useWorkflow() ?? {};
  const navigate = useRoute();
  const model = useModel();
  const { branding } = model;
  const { options } = branding;
  const phone = model.user.phone;
  const email = model.user.email;
  const defaultCardID = options.include_debit_chw23  !== "false" ? "0" : "1";
  const cardID = useParams().cardID ?? defaultCardID;
  const routeID = useParams().routeID ?? "viewdigitalcard";
  const data = { ...getDefaultData(cardID, routeID, phone, email), ...props.data ?? {} };
  const ui = { ...getDefaultUI(), ...props.ui ?? {} };
  const [ viewDidLoad, setViewDidLoad ] = useState(false);

  if(props.data) {
    if(data.cardOnFileMerchant) {
      data.howWouldYouLikeToReceiveTheSecurityToken = data.cardOnFileMerchant.howWouldYouLikeToReceiveTheSecurityToken;
    }
  }

  const controller = (state, setState) => ({
    data: state.data,
    ui: state.ui,
    update: type => (val, label) => {
      const crud = BCMSStateManagerCRUD.init(state)
        .update([ "data", "howWouldYouLikeToReceiveTheSecurityToken" ], type)
        
        if(data.cardOnFileMerchant) {
          crud.update([ "data", "cardOnFileMerchant", "howWouldYouLikeToReceiveTheSecurityToken" ], type);
        }
        crud.update([ "ui" ], ui)
        .submit(setState);
    },
    next: () => {
      if(nextStep) {
        nextStep({ data, ui });
      } else {
        navigate(`/home/securitytoken/entersecuritytoken/${ data.cardID }/${ data.howWouldYouLikeToReceiveTheSecurityToken }/${ data.routeID }`);
      }
    },
    cancel: () => {
      if(goStep) {
        // Reset the data used in the workflow.
        let clearedMerchant = undefined;
        if(data.cardOnFileSelectedTabType === "recurring") {
          clearedMerchant = RecurringPaymentMerchant.getClearedRecurringPaymentMerchant(data.cardOnFileMerchant);
        } else {
          clearedMerchant = CardOnFileMerchant.getClearedCardOnFileMerchant(data.cardOnFileMerchant);
        }
        const crud = BCMSStateManagerCRUD.init(state);
        crud
        .update([ "data", "cardOnFileMerchant" ], clearedMerchant)
        .update([ "ui" ], ui)
        .submit(setState);


        goStep(0, { data, ui });
      } else {
        navigate(`/home/recenttransactions/${ data.cardID }`);
      }
    }
  });
  
// START: Routing through this useEffect hook since this view needs to be skipped if certain
// branding is set. This view is shared in multiple workflows and will be shared in future workflows,
// so we need a single solution to skip this page.
useEffect(() => {
  let contactMethod = options.security_token_contact_chw23;
  const hasBothContactMethodsOn = contactMethod === "undefined" || contactMethod === "Both";
  
  if(!hasBothContactMethodsOn) {
    contactMethod = contactMethod.toLowerCase();
    data.howWouldYouLikeToReceiveTheSecurityToken = contactMethod === "text" ? "text" : "email";

    if(nextStep) {
      nextStep({ data, ui });
    } else {
      navigate(`/home/securitytoken/entersecuritytoken/${ data.cardID }/${ data.howWouldYouLikeToReceiveTheSecurityToken }/${ data.routeID }`);
    }
  } else {
    setViewDidLoad(true);
  }
});
// END

return (
  <>
    { viewDidLoad && 
    <StateManager state={ { data, ui } } controller={ controller }>
        <PageContent/>
    </StateManager>
    }
  </>
);
}

export const getDefaultData = (cardID, routeID, phone, email) => {
  return {
      cardID,
      howWouldYouLikeToReceiveTheSecurityToken: "email",
      routeID,
      phone,
      email
  };
}

export const getDefaultUI = () => ({

});

function PageContent(props) {
  const { data, ui, cancel, next, update } = useStateManager();
  const email = data.email ?? {};
  const phone = data.phone ?? {};
  const masked = email.slice(0, email.indexOf("@")).slice(1).replace(/[A-Za-z]/g, "*");
  const maskedEmail = `${ email.charAt(0) }${ masked }${ email.substring(email.indexOf("@"), email.length) }`;

  return (
    <div className="receive-token-view">
      <p className="row-text-container">We need to send you a security token. Please select how you would like to receive the token.</p>
      <FormContainer>
        <FormRow>
          <UIRadio label={ maskedEmail } name="receive" dataPoint="Receive_Token_Email" onChange={ update("email") } value={ data.howWouldYouLikeToReceiveTheSecurityToken === "email" }/>
        </FormRow>
        <FormRow>
          <UIRadio label={ phone } name="receive" onChange={ update("phone") } value={ data.howWouldYouLikeToReceiveTheSecurityToken === "phone" } />
        </FormRow>

        <FormRow className="flex-container flex-horizontal info-text">
          <SVGAsset id="InformationIcon" className="set-width left-inline-icon" />
          <p className="flex-width sub-text">If your email address or phone number has changed, please <DisabledPrompt><Link className="contact-link">contact us</Link></DisabledPrompt> to get them updated. </p>
        </FormRow>

        <FormButtonContainer>
          <UIPrimaryButton text="Next" onClick={ next } dataPoint="Receive_Token_Next" />
          <UICancelButton text="Cancel" onClick={ cancel }/>
        </FormButtonContainer>
      </FormContainer>
    </div>
  );
}